import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { manufacturerAPI } from '../../api'
import type { ManufacturerFormData } from '../../types'
import { optionKeys } from './useOptions'
import { productKeys } from './useProducts'

// Query key factory for manufacturers
export const manufacturerKeys = {
  all: ['manufacturers'] as const,
  lists: () => [...manufacturerKeys.all, 'list'] as const,
  list: (filters: ManufacturerFilters) => [...manufacturerKeys.lists(), filters] as const,
  public: () => [...manufacturerKeys.all, 'public'] as const,
}

export interface ManufacturerFilters {
  page?: number
  perPage?: number
  search?: string
}

// Fetch manufacturers list with filters (admin)
export function useManufacturers(filters: ManufacturerFilters = {}) {
  const {
    page = 1,
    perPage = 20,
    search = '',
  } = filters

  return useQuery({
    queryKey: manufacturerKeys.list({ page, perPage, search }),
    queryFn: () => manufacturerAPI.getManufacturers(page, perPage, search),
  })
}

// Fetch public manufacturers for ManufacturersSlider (no auth)
export function usePublicManufacturers() {
  return useQuery({
    queryKey: manufacturerKeys.public(),
    queryFn: () => manufacturerAPI.getPublicManufacturers(),
    staleTime: 5 * 60 * 1000, // 5 minutes — mutations call invalidateQueries for instant updates in the same session
  })
}

// Create manufacturer mutation
export function useCreateManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (manufacturerData: ManufacturerFormData) => manufacturerAPI.createManufacturer(manufacturerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.public() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
    },
  })
}

// Update manufacturer mutation
export function useUpdateManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ manufacturerId, manufacturerData }: { manufacturerId: number; manufacturerData: Partial<ManufacturerFormData> }) =>
      manufacturerAPI.updateManufacturer(manufacturerId, manufacturerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.public() })
      queryClient.invalidateQueries({ queryKey: optionKeys.all })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

// Delete manufacturer mutation
export function useDeleteManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (manufacturerId: number) => manufacturerAPI.deleteManufacturer(manufacturerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.public() })
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
